<template>
  <article class="loan-card">
    <div class="loan-card__header">
      <div>
        <cabinet-status :status="loan.status" />
        <h3>{{ formatMoney(loan.amount, loan.currency) }}</h3>
      </div>
      <strong v-if="isIssued">{{ formatMoney(loan.remaining_balance, loan.currency) }}<span>{{ $t('loans.remaining') }}</span></strong>
    </div>
    <div v-if="isIssued" class="loan-card__progress"><span :style="{ width: `${progress}%` }"></span></div>
    <cabinet-rows :rows="rows" />
    <h4 v-if="loan.payment_schedule.length" class="loan-card__section-title">
      {{ $t('common.scheduleCount', { count: loan.payment_schedule.length }) }}
    </h4>
    <cabinet-schedule v-if="loan.payment_schedule.length" :items="schedule" />
    <app-button
      v-if="loan.payment_schedule.length"
      class="btn--stretch loan-card__button loan-card__button--download"
      :disabled="downloading"
      @click.native="$emit('download', loan)"
    >
      {{ downloading ? $t('loans.preparingSchedule') : $t('loans.downloadSchedule') }}
    </app-button>
    <app-button
      v-if="canCancel"
      class="btn--stretch loan-card__button"
      theme="error"
      @click.native="$emit('cancel', loan)"
    >
      {{ $t('common.cancelRequest') }}
    </app-button>
  </article>
</template>

<script>
import CabinetRows from './CabinetRows'
import CabinetSchedule from './CabinetSchedule'
import CabinetStatus from './CabinetStatus'
import { formatDate, formatMoney, formatMonth, progressPercent } from './cabinetDisplay'

export default {
  name: 'LoanCard',
  components: { CabinetRows, CabinetSchedule, CabinetStatus },
  props: {
    loan: { type: Object, required: true },
    downloading: Boolean,
  },
  computed: {
    isIssued() {
      return ['disbursed', 'fully_paid', 'abandoned'].includes(this.loan.status.code)
    },
    canCancel() {
      return ['requested', 'approved'].includes(this.loan.status.code)
    },
    progress() {
      return progressPercent(this.loan.paid_amount, this.loan.amount)
    },
    rows() {
      const rows = [
        { label: this.$t('loans.paid'), value: formatMoney(this.loan.paid_amount, this.loan.currency) },
        {
          label: this.$t('loans.nextDeduction'),
          value: this.loan.next_deduction ? `${formatMonth(this.loan.next_deduction.month)} / ${formatMoney(this.loan.next_deduction.amount, this.loan.currency)}` : this.$t('common.notSpecified'),
        },
        { label: this.$t('common.requested'), value: formatDate(this.loan.created_at) },
      ]
      if (this.loan.employee_notes) rows.push({ label: this.$t('common.note'), value: this.loan.employee_notes })
      if (this.loan.decision_reason) rows.push({ label: this.$t('common.decisionReason'), value: this.loan.decision_reason })
      return rows
    },
    schedule() {
      return this.loan.payment_schedule.map(item => ({
        month: formatMonth(item.month),
        status: item.status,
        amount: formatMoney(item.amount, this.loan.currency),
      }))
    },
  },
  methods: { formatMoney },
}
</script>

<style lang="sass" scoped>
.loan-card
  padding: 20px
  border: 1px solid #e5e5e5
  border-radius: 14px

.loan-card + .loan-card
  margin-top: 16px

.loan-card__header
  display: flex
  justify-content: space-between
  gap: 14px

  h3
    margin-top: 9px
    color: #202020
    font-size: 21px
    font-weight: 600

  > strong
    color: #4f8d12
    font-size: 18px
    font-weight: 600
    text-align: right

    span
      display: block

.loan-card__progress
  height: 8px
  margin-top: 17px
  overflow: hidden
  border-radius: 5px
  background-color: #e7ece2

  span
    display: block
    height: 100%
    background-color: $color-theme

.loan-card__section-title
  margin-top: 14px
  color: #303126
  font-size: 16px
  font-weight: 700

.cabinet-schedule
  margin-top: 10px

.loan-card__button
  margin-top: 14px

.loan-card__button--download
  margin-top: 20px
</style>
